import type { PlatformMembership } from "@mountaya/studio";

import type { UserOrganization } from "@mountayaapp/api";
import type { IdentityMemberships } from "@mountayaapp/identity";

import { studioAnswerOf } from "../../../shared/rules/studioAnswer";

/**
 * OrganizationResolutionInput carries the three candidate organization ids the
 * resolver considers, in precedence order: the id from the current route param,
 * the id held in the identity store, and the id remembered in localStorage. Each
 * is null when its source has none. The resolver picks the first that names a
 * switchable membership, so a stale store/remembered id is skipped rather than
 * honored.
 */
export interface OrganizationResolutionInput {
  rememberedId: string | null;
  routeId: string | null;
  storeId: string | null;
}

/**
 * Every membership the memberships read lists is one the user has joined: an invitation waiting
 * for their answer is no membership, and is read on its own (`useMyInvitations`). So the
 * memberships ARE the organizations the user can open, switch to and work in, and no predicate
 * here filters them on a status.
 */

/**
 * WRITE_ROLES is the set of membership roles that grant write access to an
 * organization's content (owner, admin, contributor). It mirrors the backend
 * write-role guard (`CheckUserRole(WriteRoles)` in the org domain) that gates
 * cross-org create and zone edit: a viewer membership grants read but never
 * write, so it is excluded here. A Set keeps the per-membership role check O(1).
 */
const WRITE_ROLES = new Set(["owner", "admin", "contributor"]);

/**
 * isWriteMembership reports whether a membership grants write access: its role is one of the
 * write roles. It sits beside the other membership predicates so "write membership" is defined
 * once.
 */
export const isWriteMembership = (membership: UserOrganization): boolean =>
  WRITE_ROLES.has(membership.role);

/**
 * membershipFor returns the membership for the given organization id, or
 * undefined when the user has none.
 */
export const membershipFor = (
  memberships: UserOrganization[],
  organizationId: string,
): UserOrganization | undefined =>
  memberships.find((membership) => membership.organization_id === organizationId);

/**
 * resolveCurrentOrganizationId picks the organization currently in view among the user's
 * memberships. An explicit pointer (route, then store, then remembered) wins when it names a
 * membership. With no pointer match it lands on the first organization by name (a stable default
 * landing); with no membership at all it returns null (the empty-state path). Pure and
 * render-free, so the route guard (outside React) and the nav (inside React) share it.
 */
export const resolveCurrentOrganizationId = (
  memberships: UserOrganization[],
  { rememberedId, routeId, storeId }: OrganizationResolutionInput,
): string | null => {
  if (memberships.length === 0) {
    return null;
  }

  const memberIds = new Set(memberships.map((m) => m.organization_id));

  // An explicit pointer (route, then store, then remembered) wins when it names a membership.
  for (const candidate of [routeId, storeId, rememberedId]) {
    if (candidate !== null && memberIds.has(candidate)) {
      return candidate;
    }
  }

  // No pointer matched: the first organization by name, for a stable default landing.
  const sorted = [...memberships].sort((a, b) =>
    a.organization_name.localeCompare(b.organization_name),
  );

  return sorted[0]?.organization_id ?? null;
};

/**
 * summarizeMemberships reduces the membership list to what the identity store keeps of it: the
 * organizations the user is a member of, those of them whose agreement includes Map Studio, each
 * id once, in the order the list gave them, and the answer of every membership, which says what
 * the user may use of the studio there (ADR 0104).
 *
 * The shared root organization is never among them. Every account belongs to it, so counting
 * it would make everybody a member of something: it is the personal space, and the personal
 * space's answer is a read of its own. An invitation waiting for an answer is no membership, so
 * it is not among them either. The answers are kept as the platform gave them: nothing here
 * decides what a membership may use, and a feature the studio does not name is all an answer
 * loses on the way (`studioAnswerOf`). Pure, so the guards that settle the memberships (the app
 * shell and the share link) share one rule.
 *
 * @param memberships The memberships the user organizations read answered.
 * @param rootOrganizationId The shared root organization, where this deployment names one.
 * @returns The organization ids, the Map Studio ones among them, and the answer of each
 *   membership.
 */
export const summarizeMemberships = (
  memberships: readonly UserOrganization[],
  rootOrganizationId: string | null | undefined,
): IdentityMemberships => {
  const organizationIds: string[] = [];
  const studioOrganizationIds: string[] = [];
  for (const membership of memberships) {
    const id = membership.organization_id;
    if (id === rootOrganizationId || organizationIds.includes(id)) {
      continue;
    }

    organizationIds.push(id);
    if (membership.organization_capabilities.includes("studio-online")) {
      studioOrganizationIds.push(id);
    }
  }

  const studioAnswers: PlatformMembership[] = [];
  for (const membership of memberships) {
    const id = membership.organization_id;
    if (
      id === rootOrganizationId ||
      studioAnswers.some((answer) => answer.organization_id === id)
    ) {
      continue;
    }

    studioAnswers.push({ organization_id: id, ...studioAnswerOf(membership) });
  }

  return { organizationIds, studioAnswers, studioOrganizationIds };
};
